import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from './api';

interface ApiQuery<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

/**
 * Small data-fetching hook: GET on mount + manual reload. Pass null to skip.
 * `actingFor` (optional) is sent as X-Acting-For — several M11/M12/M14
 * self-service "my own records" endpoints key on it (e.g. M11's
 * supplierEntityId() requires it outright; M12/M14's /me endpoints work off
 * `nin` alone but only see ENTITY-mode records with it too). Kept as a plain
 * string param (not a generic options bag) so it's trivial to include in the
 * effect's dependency array.
 */
export function useApiQuery<T>(path: string | null, actingFor?: string): ApiQuery<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(path));
  const [tick, setTick] = useState(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    if (!path) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    api
      .get<T>(path, actingFor ? { actingFor } : undefined)
      .then((d) => {
        if (alive.current) setData(d);
      })
      .catch((e: unknown) => {
        if (alive.current) setError(e instanceof ApiError ? e.message : 'Request failed');
      })
      .finally(() => {
        if (alive.current) setLoading(false);
      });
    return () => {
      alive.current = false;
    };
  }, [path, actingFor, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, loading, reload };
}
